import { FC, useCallback } from 'react';
import { useFragment, useMutation } from 'react-relay';
import { Form, Formik } from 'formik';
import graphql from 'babel-plugin-relay/macro';

import AddIcon from '@mui/icons-material/Add';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import Stack from '@mui/material/Stack';
import TextField from 'components/TextField';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import PriceInput from 'components/PriceInput';

import { stringToNumber, useErrorHandler } from 'lib';
import { useDialog } from 'components/Dialog';

import { AddServiceOptionDialogMutation } from '__generated__/AddServiceOptionDialogMutation.graphql';
import { AddServiceOptionDialog_service$key } from '__generated__/AddServiceOptionDialog_service.graphql';

type FormFields = {
  name: string;
  scoroProductId: string;
  price: string;
};

type Props = {
  service: AddServiceOptionDialog_service$key;
  connectionId: string;
};

const initial = {
  name: '',
  scoroProductId: '',
  price: '',
};

const AddServiceOptionDialog: FC<Props> = ({ connectionId, ...props }) => {
  const { close } = useDialog();
  const errorHandler = useErrorHandler();

  const { id } = useFragment(
    graphql`
      fragment AddServiceOptionDialog_service on Service {
        id
      }
    `,
    props.service
  );

  const [add, adding] = useMutation<AddServiceOptionDialogMutation>(graphql`
    mutation AddServiceOptionDialogMutation(
      $id: ID!
      $input: CreateServiceOptionInput!
      $connections: [ID!]!
    ) {
      addServiceOptionToService(id: $id, input: $input) {
        node
          @appendNode(
            connections: $connections
            edgeTypeName: "ServiceOptionEdge"
          ) {
          ...ServiceEditVariantsRow_serviceOption
        }
      }
    }
  `);

  const handleSubmit = useCallback(
    (input: FormFields) => {
      add({
        variables: {
          id,
          input: {
            ...input,
            price: stringToNumber(input.price),
          },
          connections: [connectionId],
        },

        ...errorHandler({
          onCompleted: close,
        }),
      });
    },
    [add, close, connectionId, errorHandler, id]
  );

  return (
    <Formik<FormFields> initialValues={initial} onSubmit={handleSubmit}>
      <Form>
        <DialogTitle>Naujas paslaugos variantas</DialogTitle>

        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            Pridėkite naują paslaugos variantą
          </DialogContentText>

          <Stack spacing={4}>
            <TextField autoFocus fullWidth name="name" label="Pavadinimas" />

            <TextField
              fullWidth
              name="scoroProductId"
              label="Scoro produkto ID"
            />

            <TextField
              fullWidth
              name="price"
              label="Kaina"
              component={PriceInput}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={close} variant="text">
            Atšaukti
          </Button>

          <LoadingButton
            loading={adding}
            type="submit"
            variant="contained"
            startIcon={<AddIcon />}>
            Pridėti
          </LoadingButton>
        </DialogActions>
      </Form>
    </Formik>
  );
};

export default AddServiceOptionDialog;
